import _classCallCheck from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/createClass.js";
import _asyncToGenerator from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _regeneratorRuntime from "/opt/build/repo/node_modules/@babel/runtime/regenerator/index.js";
import firebase from '@firebase/app-compat';
import * as exp from '@firebase/auth/internal';
import { Component } from '@firebase/component';
import { isBrowserExtension, getUA, isReactNative, isNode, isIndexedDBAvailable, isIE, FirebaseError } from '@firebase/util';
var name = "@firebase/auth-compat";
var version = "0.4.2";

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var CORDOVA_ONDEVICEREADY_TIMEOUT_MS = 1000;
function _getCurrentScheme() {
  var _a;
  return ((_a = self === null || self === void 0 ? void 0 : self.location) === null || _a === void 0 ? void 0 : _a.protocol) || null;
}
/**
 * @return {boolean} Whether the current environment is http or https.
 */
function _isHttpOrHttps() {
  return _getCurrentScheme() === 'http:' || _getCurrentScheme() === 'https:';
}
/**
 * @param {?string=} ua The user agent.
 * @return {boolean} Whether the app is rendered in a mobile iOS or Android
 *     Cordova environment.
 */
function _isAndroidOrIosCordovaScheme() {
  var ua = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getUA();
  return !!((_getCurrentScheme() === 'file:' || _getCurrentScheme() === 'ionic:' || _getCurrentScheme() === 'capacitor:') && ua.toLowerCase().match(/iphone|ipad|ipod|android/));
}
/**
 * @return {boolean} Whether the environment is a native environment, where
 *     CORS checks do not apply.
 */
function _isNativeEnvironment() {
  return isReactNative() || isNode();
}
/**
 * Checks whether the user agent is IE11.
 * @return {boolean} True if it is IE11.
 */
function _isIe11() {
  return isIE() && (document === null || document === void 0 ? void 0 : document.documentMode) === 11;
}
/**
 * Checks whether the user agent is Edge.
 * @param {string} userAgent The browser user agent string.
 * @return {boolean} True if it is Edge.
 */
function _isEdge() {
  var ua = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getUA();
  return /Edge\/\d+/.test(ua);
}
/**
 * @param {?string=} opt_userAgent The navigator user agent.
 * @return {boolean} Whether local storage is not synchronized between an iframe
 *     and a popup of the same domain.
 */
function _isLocalStorageNotSynchronized() {
  var ua = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getUA();
  return _isIe11() || _isEdge(ua);
}
/** @return {boolean} Whether web storage is supported. */
function _isWebStorageSupported() {
  try {
    var storage = self.localStorage;
    var key = exp._generateEventId();
    if (storage) {
      // setItem will throw an exception if we cannot access WebStorage (e.g.,
      // Safari in private mode).
      storage['setItem'](key, '1');
      storage['removeItem'](key);
      // For browsers where iframe web storage does not synchronize with a popup
      // of the same domain, indexedDB is used for persistent storage. These
      // browsers include IE11 and Edge.
      // Make sure it is supported (IE11 and Edge private mode does not support
      // that).
      if (_isLocalStorageNotSynchronized()) {
        // In such browsers, if indexedDB is not supported, an iframe cannot be
        // notified of the popup sign in result.
        return isIndexedDBAvailable();
      }
      return true;
    }
  } catch (e) {
    // localStorage is not available from a worker. Test availability of
    // indexedDB.
    return _isWorker() && isIndexedDBAvailable();
  }
  return false;
}
/**
 * @param {?Object=} global The optional global scope.
 * @return {boolean} Whether current environment is a worker.
 */
function _isWorker() {
  // WorkerGlobalScope only defined in worker environment.
  return typeof global !== 'undefined' && 'WorkerGlobalScope' in global && 'importScripts' in global;
}
function _isPopupRedirectSupported() {
  return (_isHttpOrHttps() || isBrowserExtension() || _isAndroidOrIosCordovaScheme()) &&
  // React Native with remote debugging reports its location.protocol as
  // http.
  !_isNativeEnvironment() &&
  // Local storage has to be supported for browser popup and redirect
  // operations to work.
  _isWebStorageSupported() &&
  // DOM, popups and redirects are not supported within a worker.
  !_isWorker();
}
/** Quick check that indicates the platform *may* be Cordova */
function _isLikelyCordova() {
  return _isAndroidOrIosCordovaScheme() && typeof document !== 'undefined';
}
function _isCordova() {
  return _isCordova2.apply(this, arguments);
}
function _isCordova2() {
  _isCordova2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee17() {
    return _regeneratorRuntime.wrap(function _callee17$(_context17) {
      while (1) switch (_context17.prev = _context17.next) {
        case 0:
          if (_isLikelyCordova()) {
            _context17.next = 2;
            break;
          }
          return _context17.abrupt("return", false);
        case 2:
          return _context17.abrupt("return", new Promise(function (resolve) {
            var timeoutId = setTimeout(function () {
              // We've waited long enough; the telltale Cordova event didn't happen
              resolve(false);
            }, CORDOVA_ONDEVICEREADY_TIMEOUT_MS);
            document.addEventListener('deviceready', function () {
              clearTimeout(timeoutId);
              resolve(true);
            });
          }));
        case 3:
        case "end":
          return _context17.stop();
      }
    }, _callee17);
  }));
  return _isCordova2.apply(this, arguments);
}
function _getSelfWindow() {
  return typeof window !== 'undefined' ? window : null;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var Persistence = {
  LOCAL: 'local',
  NONE: 'none',
  SESSION: 'session'
};
var _assert$3 = exp._assert;
var PERSISTENCE_KEY = 'persistence';
/**
 * Validates that an argument is a valid persistence value. If an invalid type
 * is specified, an error is thrown synchronously.
 */
function _validatePersistenceArgument(auth, persistence) {
  _assert$3(Object.values(Persistence).includes(persistence), auth, "invalid-persistence-type" /* exp.AuthErrorCode.INVALID_PERSISTENCE */);
  // Validate if the specified type is supported in the current environment.
  if (isReactNative()) {
    // This is only supported in a browser.
    _assert$3(persistence !== Persistence.SESSION, auth, "unsupported-persistence-type" /* exp.AuthErrorCode.UNSUPPORTED_PERSISTENCE */);
    return;
  }
  if (isNode()) {
    // Only none is supported in Node.js.
    _assert$3(persistence === Persistence.NONE, auth, "unsupported-persistence-type" /* exp.AuthErrorCode.UNSUPPORTED_PERSISTENCE */);
    return;
  }
  if (_isWorker()) {
    // In a worker environment, either LOCAL or NONE are supported.
    // If indexedDB not supported and LOCAL provided, throw an error
    _assert$3(persistence === Persistence.NONE || persistence === Persistence.LOCAL && isIndexedDBAvailable(), auth, "unsupported-persistence-type" /* exp.AuthErrorCode.UNSUPPORTED_PERSISTENCE */);
    return;
  }
  // This is restricted by what the browser supports.
  _assert$3(persistence === Persistence.NONE || _isWebStorageSupported(), auth, "unsupported-persistence-type" /* exp.AuthErrorCode.UNSUPPORTED_PERSISTENCE */);
}
function _savePersistenceForRedirect(_x) {
  return _savePersistenceForRedirect2.apply(this, arguments);
}
function _savePersistenceForRedirect2() {
  _savePersistenceForRedirect2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee18(auth) {
    var session, key;
    return _regeneratorRuntime.wrap(function _callee18$(_context18) {
      while (1) switch (_context18.prev = _context18.next) {
        case 0:
          _context18.next = 2;
          return auth._initializationPromise;
        case 2:
          session = getSessionStorageIfAvailable();
          key = exp._persistenceKeyName(PERSISTENCE_KEY, auth.config.apiKey, auth.name);
          if (session) {
            session.setItem(key, auth._getPersistence());
          }
        case 5:
        case "end":
          return _context18.stop();
      }
    }, _callee18);
  }));
  return _savePersistenceForRedirect2.apply(this, arguments);
}
function _getPersistencesFromRedirect(apiKey, appName) {
  var session = getSessionStorageIfAvailable();
  if (!session) {
    return [];
  }
  var key = exp._persistenceKeyName(PERSISTENCE_KEY, apiKey, appName);
  var persistence = session.getItem(key);
  switch (persistence) {
    case Persistence.NONE:
      return [exp.inMemoryPersistence];
    case Persistence.LOCAL:
      return [exp.indexedDBLocalPersistence, exp.browserSessionPersistence];
    case Persistence.SESSION:
      return [exp.browserSessionPersistence];
    default:
      return [];
  }
}
/** Returns session storage, or null if the property access errors */
function getSessionStorageIfAvailable() {
  var _a;
  try {
    return ((_a = _getSelfWindow()) === null || _a === void 0 ? void 0 : _a.sessionStorage) || null;
  } catch (e) {
    return null;
  }
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var _assert$2 = exp._assert;
/** Platform-agnostic popup-redirect resolver */
var CompatPopupRedirectResolver = /*#__PURE__*/function () {
  function CompatPopupRedirectResolver() {
    _classCallCheck(this, CompatPopupRedirectResolver);
    // Create both resolvers for dynamic resolution later
    this.browserResolver = exp._getInstance(exp.browserPopupRedirectResolver);
    this.cordovaResolver = exp._getInstance(exp.cordovaPopupRedirectResolver);
    // The actual resolver in use: either browserResolver or cordovaResolver.
    this.underlyingResolver = null;
    this._redirectPersistence = exp.browserSessionPersistence;
    this._completeRedirectFn = exp._getRedirectResult;
    this._overrideRedirectResult = exp._overrideRedirectResult;
  }
  _createClass(CompatPopupRedirectResolver, [{
    key: "_initialize",
    value: function () {
      var _initialize2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(auth) {
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.next = 2;
              return this.selectUnderlyingResolver();
            case 2:
              return _context.abrupt("return", this.assertedUnderlyingResolver._initialize(auth));
            case 3:
            case "end":
              return _context.stop();
          }
        }, _callee, this);
      }));
      function _initialize(_x2) {
        return _initialize2.apply(this, arguments);
      }
      return _initialize;
    }()
  }, {
    key: "_openPopup",
    value: function () {
      var _openPopup2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(auth, provider, authType, eventId) {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              _context2.next = 2;
              return this.selectUnderlyingResolver();
            case 2:
              return _context2.abrupt("return", this.assertedUnderlyingResolver._openPopup(auth, provider, authType, eventId));
            case 3:
            case "end":
              return _context2.stop();
          }
        }, _callee2, this);
      }));
      function _openPopup(_x3, _x4, _x5, _x6) {
        return _openPopup2.apply(this, arguments);
      }
      return _openPopup;
    }()
  }, {
    key: "_openRedirect",
    value: function () {
      var _openRedirect2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(auth, provider, authType, eventId) {
        return _regeneratorRuntime.wrap(function _callee3$(_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              _context3.next = 2;
              return this.selectUnderlyingResolver();
            case 2:
              return _context3.abrupt("return", this.assertedUnderlyingResolver._openRedirect(auth, provider, authType, eventId));
            case 3:
            case "end":
              return _context3.stop();
          }
        }, _callee3, this);
      }));
      function _openRedirect(_x7, _x8, _x9, _x10) {
        return _openRedirect2.apply(this, arguments);
      }
      return _openRedirect;
    }()
  }, {
    key: "_isIframeWebStorageSupported",
    value: function _isIframeWebStorageSupported(auth, cb) {
      this.assertedUnderlyingResolver._isIframeWebStorageSupported(auth, cb);
    }
  }, {
    key: "_originValidation",
    value: function _originValidation(auth) {
      return this.assertedUnderlyingResolver._originValidation(auth);
    }
  }, {
    key: "_shouldInitProactively",
    get: function get() {
      return _isLikelyCordova() || this.browserResolver._shouldInitProactively;
    }
  }, {
    key: "assertedUnderlyingResolver",
    get: function get() {
      _assert$2(this.underlyingResolver, "internal-error" /* exp.AuthErrorCode.INTERNAL_ERROR */);
      return this.underlyingResolver;
    }
  }, {
    key: "selectUnderlyingResolver",
    value: function () {
      var _selectUnderlyingResolver = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4() {
        var isCordova;
        return _regeneratorRuntime.wrap(function _callee4$(_context4) {
          while (1) switch (_context4.prev = _context4.next) {
            case 0:
              if (!this.underlyingResolver) {
                _context4.next = 2;
                break;
              }
              return _context4.abrupt("return");
            case 2:
              _context4.next = 4;
              return _isCordova();
            case 4:
              isCordova = _context4.sent;
              this.underlyingResolver = isCordova ? this.cordovaResolver : this.browserResolver;
            case 6:
            case "end":
              return _context4.stop();
          }
        }, _callee4, this);
      }));
      function selectUnderlyingResolver() {
        return _selectUnderlyingResolver.apply(this, arguments);
      }
      return selectUnderlyingResolver;
    }()
  }]);
  return CompatPopupRedirectResolver;
}();
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function unwrap(object) {
  return object.unwrap();
}
function wrapped(object) {
  return object.wrapped();
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function credentialFromResponse(userCredential) {
  return credentialFromObject(userCredential);
}
function attachExtraErrorFields(auth, e) {
  var _a;
  // The response contains all fields from the server which may or may not
  // actually match the underlying type
  var response = (_a = e.customData) === null || _a === void 0 ? void 0 : _a._tokenResponse;
  if ((e === null || e === void 0 ? void 0 : e.code) === 'auth/multi-factor-auth-required') {
    var mfaErr = e;
    mfaErr.resolver = new MultiFactorResolver(auth, exp.getMultiFactorResolver(auth, e));
  } else if (response) {
    var credential = credentialFromObject(e);
    var credErr = e;
    if (credential) {
      credErr.credential = credential;
      credErr.tenantId = response.tenantId || undefined;
      credErr.email = response.email || undefined;
      credErr.phoneNumber = response.phoneNumber || undefined;
    }
  }
}
function credentialFromObject(object) {
  var _ref = object instanceof FirebaseError ? object.customData : object,
    _tokenResponse = _ref._tokenResponse;
  if (!_tokenResponse) {
    return null;
  }
  // Handle phone Auth credential responses, as they have a different format
  // from other backend responses (i.e. no providerId). This is also only the
  // case for user credentials (does not work for errors).
  if (!(object instanceof FirebaseError)) {
    if ('temporaryProof' in _tokenResponse && 'phoneNumber' in _tokenResponse) {
      return exp.PhoneAuthProvider.credentialFromResult(object);
    }
  }
  var providerId = _tokenResponse.providerId;
  // Email and password is not supported as there is no situation where the
  // server would return the password to the client.
  if (!providerId || providerId === exp.ProviderId.PASSWORD) {
    return null;
  }
  var provider;
  switch (providerId) {
    case exp.ProviderId.GOOGLE:
      provider = exp.GoogleAuthProvider;
      break;
    case exp.ProviderId.FACEBOOK:
      provider = exp.FacebookAuthProvider;
      break;
    case exp.ProviderId.GITHUB:
      provider = exp.GithubAuthProvider;
      break;
    case exp.ProviderId.TWITTER:
      provider = exp.TwitterAuthProvider;
      break;
    default:
      var oauthIdToken = _tokenResponse.oauthIdToken,
        oauthAccessToken = _tokenResponse.oauthAccessToken,
        oauthTokenSecret = _tokenResponse.oauthTokenSecret,
        pendingToken = _tokenResponse.pendingToken,
        nonce = _tokenResponse.nonce;
      if (!oauthAccessToken && !oauthTokenSecret && !oauthIdToken && !pendingToken) {
        return null;
      }
      // TODO(avolkovi): uncomment this and get it working with SAML & OIDC
      if (pendingToken) {
        if (providerId.startsWith('saml.')) {
          return exp.SAMLAuthCredential._create(providerId, pendingToken);
        } else {
          // OIDC and non-default providers excluding Twitter.
          return exp.OAuthCredential._fromParams({
            providerId: providerId,
            signInMethod: providerId,
            pendingToken: pendingToken,
            idToken: oauthIdToken,
            accessToken: oauthAccessToken
          });
        }
      }
      return new exp.OAuthProvider(providerId).credential({
        idToken: oauthIdToken,
        accessToken: oauthAccessToken,
        rawNonce: nonce
      });
  }
  return object instanceof FirebaseError ? provider.credentialFromError(object) : provider.credentialFromResult(object);
}
function convertCredential(auth, credentialPromise) {
  return credentialPromise.catch(function (e) {
    if (e instanceof FirebaseError) {
      attachExtraErrorFields(auth, e);
    }
    throw e;
  }).then(function (credential) {
    var operationType = credential.operationType;
    var user = credential.user;
    return {
      operationType: operationType,
      credential: credentialFromResponse(credential),
      additionalUserInfo: exp.getAdditionalUserInfo(credential),
      user: User.getOrCreate(user)
    };
  });
}
function convertConfirmationResult(_x11, _x12) {
  return _convertConfirmationResult.apply(this, arguments);
}
function _convertConfirmationResult() {
  _convertConfirmationResult = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee19(auth, confirmationResultPromise) {
    var confirmationResultExp;
    return _regeneratorRuntime.wrap(function _callee19$(_context19) {
      while (1) switch (_context19.prev = _context19.next) {
        case 0:
          _context19.next = 2;
          return confirmationResultPromise;
        case 2:
          confirmationResultExp = _context19.sent;
          return _context19.abrupt("return", {
            verificationId: confirmationResultExp.verificationId,
            confirm: function confirm(verificationCode) {
              return convertCredential(auth, confirmationResultExp.confirm(verificationCode));
            }
          });
        case 4:
        case "end":
          return _context19.stop();
      }
    }, _callee19);
  }));
  return _convertConfirmationResult.apply(this, arguments);
}
var MultiFactorResolver = /*#__PURE__*/function () {
  function MultiFactorResolver(auth, resolver) {
    _classCallCheck(this, MultiFactorResolver);
    this.resolver = resolver;
    this.auth = wrapped(auth);
  }
  _createClass(MultiFactorResolver, [{
    key: "session",
    get: function get() {
      return this.resolver.session;
    }
  }, {
    key: "hints",
    get: function get() {
      return this.resolver.hints;
    }
  }, {
    key: "resolveSignIn",
    value: function resolveSignIn(assertion) {
      return convertCredential(unwrap(this.auth), this.resolver.resolveSignIn(assertion));
    }
  }]);
  return MultiFactorResolver;
}();
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var User = /*#__PURE__*/function () {
  function User(_delegate) {
    _classCallCheck(this, User);
    this._delegate = _delegate;
    this.multiFactor = exp.multiFactor(_delegate);
  }
  _createClass(User, [{
    key: "delete",
    value: function _delete() {
      return this._delegate.delete();
    }
  }, {
    key: "reload",
    value: function reload() {
      return this._delegate.reload();
    }
  }, {
    key: "toJSON",
    value: function toJSON() {
      return this._delegate.toJSON();
    }
  }, {
    key: "getIdTokenResult",
    value: function getIdTokenResult(forceRefresh) {
      return this._delegate.getIdTokenResult(forceRefresh);
    }
  }, {
    key: "getIdToken",
    value: function getIdToken(forceRefresh) {
      return this._delegate.getIdToken(forceRefresh);
    }
  }, {
    key: "linkAndRetrieveDataWithCredential",
    value: function linkAndRetrieveDataWithCredential(credential) {
      return this.linkWithCredential(credential);
    }
  }, {
    key: "linkWithCredential",
    value: function () {
      var _linkWithCredential = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(credential) {
        return _regeneratorRuntime.wrap(function _callee5$(_context5) {
          while (1) switch (_context5.prev = _context5.next) {
            case 0:
              return _context5.abrupt("return", convertCredential(this.auth, exp.linkWithCredential(this._delegate, credential)));
            case 1:
            case "end":
              return _context5.stop();
          }
        }, _callee5, this);
      }));
      function linkWithCredential(_x13) {
        return _linkWithCredential.apply(this, arguments);
      }
      return linkWithCredential;
    }()
  }, {
    key: "linkWithPhoneNumber",
    value: function () {
      var _linkWithPhoneNumber = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6(phoneNumber, applicationVerifier) {
        return _regeneratorRuntime.wrap(function _callee6$(_context6) {
          while (1) switch (_context6.prev = _context6.next) {
            case 0:
              return _context6.abrupt("return", convertConfirmationResult(this.auth, exp.linkWithPhoneNumber(this._delegate, phoneNumber, applicationVerifier)));
            case 1:
            case "end":
              return _context6.stop();
          }
        }, _callee6, this);
      }));
      function linkWithPhoneNumber(_x14, _x15) {
        return _linkWithPhoneNumber.apply(this, arguments);
      }
      return linkWithPhoneNumber;
    }()
  }, {
    key: "linkWithPopup",
    value: function () {
      var _linkWithPopup = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7(provider) {
        return _regeneratorRuntime.wrap(function _callee7$(_context7) {
          while (1) switch (_context7.prev = _context7.next) {
            case 0:
              return _context7.abrupt("return", convertCredential(this.auth, exp.linkWithPopup(this._delegate, provider, CompatPopupRedirectResolver)));
            case 1:
            case "end":
              return _context7.stop();
          }
        }, _callee7, this);
      }));
      function linkWithPopup(_x16) {
        return _linkWithPopup.apply(this, arguments);
      }
      return linkWithPopup;
    }()
  }, {
    key: "linkWithRedirect",
    value: function () {
      var _linkWithRedirect = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8(provider) {
        return _regeneratorRuntime.wrap(function _callee8$(_context8) {
          while (1) switch (_context8.prev = _context8.next) {
            case 0:
              _context8.next = 2;
              return _savePersistenceForRedirect(exp._castAuth(this.auth));
            case 2:
              return _context8.abrupt("return", exp.linkWithRedirect(this._delegate, provider, CompatPopupRedirectResolver));
            case 3:
            case "end":
              return _context8.stop();
          }
        }, _callee8, this);
      }));
      function linkWithRedirect(_x17) {
        return _linkWithRedirect.apply(this, arguments);
      }
      return linkWithRedirect;
    }()
  }, {
    key: "reauthenticateAndRetrieveDataWithCredential",
    value: function reauthenticateAndRetrieveDataWithCredential(credential) {
      return this.reauthenticateWithCredential(credential);
    }
  }, {
    key: "reauthenticateWithCredential",
    value: function () {
      var _reauthenticateWithCredential = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee9(credential) {
        return _regeneratorRuntime.wrap(function _callee9$(_context9) {
          while (1) switch (_context9.prev = _context9.next) {
            case 0:
              return _context9.abrupt("return", convertCredential(this.auth, exp.reauthenticateWithCredential(this._delegate, credential)));
            case 1:
            case "end":
              return _context9.stop();
          }
        }, _callee9, this);
      }));
      function reauthenticateWithCredential(_x18) {
        return _reauthenticateWithCredential.apply(this, arguments);
      }
      return reauthenticateWithCredential;
    }()
  }, {
    key: "reauthenticateWithPhoneNumber",
    value: function reauthenticateWithPhoneNumber(phoneNumber, applicationVerifier) {
      return convertConfirmationResult(this.auth, exp.reauthenticateWithPhoneNumber(this._delegate, phoneNumber, applicationVerifier));
    }
  }, {
    key: "reauthenticateWithPopup",
    value: function reauthenticateWithPopup(provider) {
      return convertCredential(this.auth, exp.reauthenticateWithPopup(this._delegate, provider, CompatPopupRedirectResolver));
    }
  }, {
    key: "reauthenticateWithRedirect",
    value: function () {
      var _reauthenticateWithRedirect = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee10(provider) {
        return _regeneratorRuntime.wrap(function _callee10$(_context10) {
          while (1) switch (_context10.prev = _context10.next) {
            case 0:
              _context10.next = 2;
              return _savePersistenceForRedirect(exp._castAuth(this.auth));
            case 2:
              return _context10.abrupt("return", exp.reauthenticateWithRedirect(this._delegate, provider, CompatPopupRedirectResolver));
            case 3:
            case "end":
              return _context10.stop();
          }
        }, _callee10, this);
      }));
      function reauthenticateWithRedirect(_x19) {
        return _reauthenticateWithRedirect.apply(this, arguments);
      }
      return reauthenticateWithRedirect;
    }()
  }, {
    key: "sendEmailVerification",
    value: function sendEmailVerification(actionCodeSettings) {
      return exp.sendEmailVerification(this._delegate, actionCodeSettings);
    }
  }, {
    key: "unlink",
    value: function () {
      var _unlink = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee11(providerId) {
        return _regeneratorRuntime.wrap(function _callee11$(_context11) {
          while (1) switch (_context11.prev = _context11.next) {
            case 0:
              _context11.next = 2;
              return exp.unlink(this._delegate, providerId);
            case 2:
              return _context11.abrupt("return", this);
            case 3:
            case "end":
              return _context11.stop();
          }
        }, _callee11, this);
      }));
      function unlink(_x20) {
        return _unlink.apply(this, arguments);
      }
      return unlink;
    }()
  }, {
    key: "updateEmail",
    value: function updateEmail(newEmail) {
      return exp.updateEmail(this._delegate, newEmail);
    }
  }, {
    key: "updatePassword",
    value: function updatePassword(newPassword) {
      return exp.updatePassword(this._delegate, newPassword);
    }
  }, {
    key: "updatePhoneNumber",
    value: function updatePhoneNumber(phoneCredential) {
      return exp.updatePhoneNumber(this._delegate, phoneCredential);
    }
  }, {
    key: "updateProfile",
    value: function updateProfile(profile) {
      return exp.updateProfile(this._delegate, profile);
    }
  }, {
    key: "verifyBeforeUpdateEmail",
    value: function verifyBeforeUpdateEmail(newEmail, actionCodeSettings) {
      return exp.verifyBeforeUpdateEmail(this._delegate, newEmail, actionCodeSettings);
    }
  }, {
    key: "emailVerified",
    get: function get() {
      return this._delegate.emailVerified;
    }
  }, {
    key: "isAnonymous",
    get: function get() {
      return this._delegate.isAnonymous;
    }
  }, {
    key: "metadata",
    get: function get() {
      return this._delegate.metadata;
    }
  }, {
    key: "phoneNumber",
    get: function get() {
      return this._delegate.phoneNumber;
    }
  }, {
    key: "providerData",
    get: function get() {
      return this._delegate.providerData;
    }
  }, {
    key: "refreshToken",
    get: function get() {
      return this._delegate.refreshToken;
    }
  }, {
    key: "tenantId",
    get: function get() {
      return this._delegate.tenantId;
    }
  }, {
    key: "displayName",
    get: function get() {
      return this._delegate.displayName;
    }
  }, {
    key: "email",
    get: function get() {
      return this._delegate.email;
    }
  }, {
    key: "photoURL",
    get: function get() {
      return this._delegate.photoURL;
    }
  }, {
    key: "providerId",
    get: function get() {
      return this._delegate.providerId;
    }
  }, {
    key: "uid",
    get: function get() {
      return this._delegate.uid;
    }
  }, {
    key: "auth",
    get: function get() {
      return this._delegate.auth;
    }
  }], [{
    key: "getOrCreate",
    value: function getOrCreate(user) {
      if (!User.USER_MAP.has(user)) {
        User.USER_MAP.set(user, new User(user));
      }
      return User.USER_MAP.get(user);
    }
  }]);
  return User;
}(); // Maintain a map so that there's always a 1:1 mapping between new User and
// legacy compat users
User.USER_MAP = new WeakMap();

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var _assert$1 = exp._assert;
var Auth = /*#__PURE__*/function () {
  function Auth(app, provider) {
    _classCallCheck(this, Auth);
    this.app = app;
    if (provider.isInitialized()) {
      this._delegate = provider.getImmediate();
      this.linkUnderlyingAuth();
      return;
    }
    var apiKey = app.options.apiKey;
    // TODO: platform needs to be determined using heuristics
    _assert$1(apiKey, "invalid-api-key" /* exp.AuthErrorCode.INVALID_API_KEY */, {
      appName: app.name
    });
    // TODO: platform needs to be determined using heuristics
    _assert$1(apiKey, "invalid-api-key" /* exp.AuthErrorCode.INVALID_API_KEY */, {
      appName: app.name
    });
    // Only use a popup/redirect resolver in browser environments
    var resolver = typeof window !== 'undefined' ? CompatPopupRedirectResolver : undefined;
    this._delegate = provider.initialize({
      options: {
        persistence: buildPersistenceHierarchy(apiKey, app.name),
        popupRedirectResolver: resolver
      }
    });
    this._delegate._updateErrorMap(exp.debugErrorMap);
    this.linkUnderlyingAuth();
  }
  _createClass(Auth, [{
    key: "emulatorConfig",
    get: function get() {
      return this._delegate.emulatorConfig;
    }
  }, {
    key: "currentUser",
    get: function get() {
      if (!this._delegate.currentUser) {
        return null;
      }
      return User.getOrCreate(this._delegate.currentUser);
    }
  }, {
    key: "languageCode",
    get: function get() {
      return this._delegate.languageCode;
    },
    set: function set(languageCode) {
      this._delegate.languageCode = languageCode;
    }
  }, {
    key: "settings",
    get: function get() {
      return this._delegate.settings;
    }
  }, {
    key: "tenantId",
    get: function get() {
      return this._delegate.tenantId;
    },
    set: function set(tid) {
      this._delegate.tenantId = tid;
    }
  }, {
    key: "useDeviceLanguage",
    value: function useDeviceLanguage() {
      this._delegate.useDeviceLanguage();
    }
  }, {
    key: "signOut",
    value: function signOut() {
      return this._delegate.signOut();
    }
  }, {
    key: "useEmulator",
    value: function useEmulator(url, options) {
      exp.connectAuthEmulator(this._delegate, url, options);
    }
  }, {
    key: "applyActionCode",
    value: function applyActionCode(code) {
      return exp.applyActionCode(this._delegate, code);
    }
  }, {
    key: "checkActionCode",
    value: function checkActionCode(code) {
      return exp.checkActionCode(this._delegate, code);
    }
  }, {
    key: "confirmPasswordReset",
    value: function confirmPasswordReset(code, newPassword) {
      return exp.confirmPasswordReset(this._delegate, code, newPassword);
    }
  }, {
    key: "createUserWithEmailAndPassword",
    value: function () {
      var _createUserWithEmailAndPassword = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee12(email, password) {
        return _regeneratorRuntime.wrap(function _callee12$(_context12) {
          while (1) switch (_context12.prev = _context12.next) {
            case 0:
              return _context12.abrupt("return", convertCredential(this._delegate, exp.createUserWithEmailAndPassword(this._delegate, email, password)));
            case 1:
            case "end":
              return _context12.stop();
          }
        }, _callee12, this);
      }));
      function createUserWithEmailAndPassword(_x21, _x22) {
        return _createUserWithEmailAndPassword.apply(this, arguments);
      }
      return createUserWithEmailAndPassword;
    }()
  }, {
    key: "fetchProvidersForEmail",
    value: function fetchProvidersForEmail(email) {
      return this.fetchSignInMethodsForEmail(email);
    }
  }, {
    key: "fetchSignInMethodsForEmail",
    value: function fetchSignInMethodsForEmail(email) {
      return exp.fetchSignInMethodsForEmail(this._delegate, email);
    }
  }, {
    key: "isSignInWithEmailLink",
    value: function isSignInWithEmailLink(emailLink) {
      return exp.isSignInWithEmailLink(this._delegate, emailLink);
    }
  }, {
    key: "getRedirectResult",
    value: function () {
      var _getRedirectResult = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee13() {
        var credential;
        return _regeneratorRuntime.wrap(function _callee13$(_context13) {
          while (1) switch (_context13.prev = _context13.next) {
            case 0:
              _assert$1(_isPopupRedirectSupported(), this._delegate, "operation-not-supported-in-this-environment" /* exp.AuthErrorCode.OPERATION_NOT_SUPPORTED */);
              _context13.next = 3;
              return exp.getRedirectResult(this._delegate, CompatPopupRedirectResolver);
            case 3:
              credential = _context13.sent;
              if (credential) {
                _context13.next = 6;
                break;
              }
              return _context13.abrupt("return", {
                credential: null,
                user: null
              });
            case 6:
              return _context13.abrupt("return", convertCredential(this._delegate, Promise.resolve(credential)));
            case 7:
            case "end":
              return _context13.stop();
          }
        }, _callee13, this);
      }));
      function getRedirectResult() {
        return _getRedirectResult.apply(this, arguments);
      }
      return getRedirectResult;
    }() // This function should only be called by frameworks (e.g. FirebaseUI-web) to log their usage.
    // It is not intended for direct use by developer apps. NO jsdoc here to intentionally leave it
    // out of autogenerated documentation pages to reduce accidental misuse.
  }, {
    key: "addFrameworkForLogging",
    value: function addFrameworkForLogging(framework) {
      exp.addFrameworkForLogging(this._delegate, framework);
    }
  }, {
    key: "onAuthStateChanged",
    value: function onAuthStateChanged(nextOrObserver, errorFn, completed) {
      var _wrapObservers = wrapObservers(nextOrObserver, errorFn, completed),
        next = _wrapObservers.next,
        error = _wrapObservers.error,
        complete = _wrapObservers.complete;
      return this._delegate.onAuthStateChanged(next, error, complete);
    }
  }, {
    key: "onIdTokenChanged",
    value: function onIdTokenChanged(nextOrObserver, errorFn, completed) {
      var _wrapObservers2 = wrapObservers(nextOrObserver, errorFn, completed),
        next = _wrapObservers2.next,
        error = _wrapObservers2.error,
        complete = _wrapObservers2.complete;
      return this._delegate.onIdTokenChanged(next, error, complete);
    }
  }, {
    key: "sendSignInLinkToEmail",
    value: function sendSignInLinkToEmail(email, actionCodeSettings) {
      return exp.sendSignInLinkToEmail(this._delegate, email, actionCodeSettings);
    }
  }, {
    key: "sendPasswordResetEmail",
    value: function sendPasswordResetEmail(email, actionCodeSettings) {
      return exp.sendPasswordResetEmail(this._delegate, email, actionCodeSettings || undefined);
    }
  }, {
    key: "setPersistence",
    value: function () {
      var _setPersistence = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee14(persistence) {
        var converted, isIndexedDBFullySupported;
        return _regeneratorRuntime.wrap(function _callee14$(_context14) {
          while (1) switch (_context14.prev = _context14.next) {
            case 0:
              _validatePersistenceArgument(this._delegate, persistence);
              _context14.t0 = persistence;
              _context14.next = _context14.t0 === Persistence.SESSION ? 4 : _context14.t0 === Persistence.LOCAL ? 6 : _context14.t0 === Persistence.NONE ? 11 : 13;
              break;
            case 4:
              converted = exp.browserSessionPersistence;
              return _context14.abrupt("break", 14);
            case 6:
              _context14.next = 8;
              return exp._getInstance(exp.indexedDBLocalPersistence)._isAvailable();
            case 8:
              isIndexedDBFullySupported = _context14.sent;
              converted = isIndexedDBFullySupported ? exp.indexedDBLocalPersistence : exp.browserLocalPersistence;
              return _context14.abrupt("break", 14);
            case 11:
              converted = exp.inMemoryPersistence;
              return _context14.abrupt("break", 14);
            case 13:
              return _context14.abrupt("return", exp._fail("argument-error" /* exp.AuthErrorCode.ARGUMENT_ERROR */, {
                appName: this._delegate.name
              }));
            case 14:
              return _context14.abrupt("return", this._delegate.setPersistence(converted));
            case 15:
            case "end":
              return _context14.stop();
          }
        }, _callee14, this);
      }));
      function setPersistence(_x23) {
        return _setPersistence.apply(this, arguments);
      }
      return setPersistence;
    }()
  }, {
    key: "signInAndRetrieveDataWithCredential",
    value: function signInAndRetrieveDataWithCredential(credential) {
      return this.signInWithCredential(credential);
    }
  }, {
    key: "signInAnonymously",
    value: function signInAnonymously() {
      return convertCredential(this._delegate, exp.signInAnonymously(this._delegate));
    }
  }, {
    key: "signInWithCredential",
    value: function signInWithCredential(credential) {
      return convertCredential(this._delegate, exp.signInWithCredential(this._delegate, credential));
    }
  }, {
    key: "signInWithCustomToken",
    value: function signInWithCustomToken(token) {
      return convertCredential(this._delegate, exp.signInWithCustomToken(this._delegate, token));
    }
  }, {
    key: "signInWithEmailAndPassword",
    value: function signInWithEmailAndPassword(email, password) {
      return convertCredential(this._delegate, exp.signInWithEmailAndPassword(this._delegate, email, password));
    }
  }, {
    key: "signInWithEmailLink",
    value: function signInWithEmailLink(email, emailLink) {
      return convertCredential(this._delegate, exp.signInWithEmailLink(this._delegate, email, emailLink));
    }
  }, {
    key: "signInWithPhoneNumber",
    value: function signInWithPhoneNumber(phoneNumber, applicationVerifier) {
      return convertConfirmationResult(this._delegate, exp.signInWithPhoneNumber(this._delegate, phoneNumber, applicationVerifier));
    }
  }, {
    key: "signInWithPopup",
    value: function () {
      var _signInWithPopup = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee15(provider) {
        return _regeneratorRuntime.wrap(function _callee15$(_context15) {
          while (1) switch (_context15.prev = _context15.next) {
            case 0:
              _assert$1(_isPopupRedirectSupported(), this._delegate, "operation-not-supported-in-this-environment" /* exp.AuthErrorCode.OPERATION_NOT_SUPPORTED */);
              return _context15.abrupt("return", convertCredential(this._delegate, exp.signInWithPopup(this._delegate, provider, CompatPopupRedirectResolver)));
            case 2:
            case "end":
              return _context15.stop();
          }
        }, _callee15, this);
      }));
      function signInWithPopup(_x24) {
        return _signInWithPopup.apply(this, arguments);
      }
      return signInWithPopup;
    }()
  }, {
    key: "signInWithRedirect",
    value: function () {
      var _signInWithRedirect = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee16(provider) {
        return _regeneratorRuntime.wrap(function _callee16$(_context16) {
          while (1) switch (_context16.prev = _context16.next) {
            case 0:
              _assert$1(_isPopupRedirectSupported(), this._delegate, "operation-not-supported-in-this-environment" /* exp.AuthErrorCode.OPERATION_NOT_SUPPORTED */);
              _context16.next = 3;
              return _savePersistenceForRedirect(this._delegate);
            case 3:
              return _context16.abrupt("return", exp.signInWithRedirect(this._delegate, provider, CompatPopupRedirectResolver));
            case 4:
            case "end":
              return _context16.stop();
          }
        }, _callee16, this);
      }));
      function signInWithRedirect(_x25) {
        return _signInWithRedirect.apply(this, arguments);
      }
      return signInWithRedirect;
    }()
  }, {
    key: "updateCurrentUser",
    value: function updateCurrentUser(user) {
      // remove ts-ignore once overloads are defined for exp functions to accept compat objects
      // @ts-ignore
      return this._delegate.updateCurrentUser(user);
    }
  }, {
    key: "verifyPasswordResetCode",
    value: function verifyPasswordResetCode(code) {
      return exp.verifyPasswordResetCode(this._delegate, code);
    }
  }, {
    key: "unwrap",
    value: function unwrap() {
      return this._delegate;
    }
  }, {
    key: "_delete",
    value: function _delete() {
      return this._delegate._delete();
    }
  }, {
    key: "linkUnderlyingAuth",
    value: function linkUnderlyingAuth() {
      var _this = this;
      this._delegate.wrapped = function () {
        return _this;
      };
    }
  }]);
  return Auth;
}();
Auth.Persistence = Persistence;
function wrapObservers(nextOrObserver, error, complete) {
  var next = nextOrObserver;
  if (typeof nextOrObserver !== 'function') {
    next = nextOrObserver.next;
    error = nextOrObserver.error;
    complete = nextOrObserver.complete;
  }
  // We know 'next' is now a function
  var oldNext = next;
  var newNext = function newNext(user) {
    return oldNext(user && User.getOrCreate(user));
  };
  return {
    next: newNext,
    error: error,
    complete: complete
  };
}
function buildPersistenceHierarchy(apiKey, appName) {
  // Note this is slightly different behavior: in this case, the stored
  // persistence is checked *first* rather than last. This is because we want
  // to prefer stored persistence type in the hierarchy. This is an empty
  // array if window is not available or there is no pending redirect
  var persistences = _getPersistencesFromRedirect(apiKey, appName);
  // If "self" is available, add indexedDB
  if (typeof self !== 'undefined' && !persistences.includes(exp.indexedDBLocalPersistence)) {
    persistences.push(exp.indexedDBLocalPersistence);
  }
  // If "window" is available, add HTML Storage persistences
  if (typeof window !== 'undefined') {
    for (var _i = 0, _arr = [exp.browserLocalPersistence, exp.browserSessionPersistence]; _i < _arr.length; _i++) {
      var persistence = _arr[_i];
      if (!persistences.includes(persistence)) {
        persistences.push(persistence);
      }
    }
  }
  // Add in-memory as a final fallback
  if (!persistences.includes(exp.inMemoryPersistence)) {
    persistences.push(exp.inMemoryPersistence);
  }
  return persistences;
}

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var PhoneAuthProvider = /*#__PURE__*/function () {
  function PhoneAuthProvider() {
    _classCallCheck(this, PhoneAuthProvider);
    this.providerId = 'phone';
    // TODO: remove ts-ignore when moving types from auth-types to auth-compat
    // @ts-ignore
    this._delegate = new exp.PhoneAuthProvider(unwrap(firebase.auth()));
  }
  _createClass(PhoneAuthProvider, [{
    key: "verifyPhoneNumber",
    value: function verifyPhoneNumber(phoneInfoOptions, applicationVerifier) {
      return this._delegate.verifyPhoneNumber(
      // The implementation matches but the types are subtly incompatible
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      phoneInfoOptions, applicationVerifier);
    }
  }, {
    key: "unwrap",
    value: function unwrap() {
      return this._delegate;
    }
  }], [{
    key: "credential",
    value: function credential(verificationId, verificationCode) {
      return exp.PhoneAuthProvider.credential(verificationId, verificationCode);
    }
  }]);
  return PhoneAuthProvider;
}();
PhoneAuthProvider.PHONE_SIGN_IN_METHOD = exp.PhoneAuthProvider.PHONE_SIGN_IN_METHOD;
PhoneAuthProvider.PROVIDER_ID = exp.PhoneAuthProvider.PROVIDER_ID;

/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var _assert = exp._assert;
var RecaptchaVerifier = /*#__PURE__*/function () {
  function RecaptchaVerifier(container, parameters) {
    var app = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : firebase.app();
    _classCallCheck(this, RecaptchaVerifier);
    var _a;
    // API key is required for web client RPC calls.
    _assert((_a = app.options) === null || _a === void 0 ? void 0 : _a.apiKey, "invalid-api-key" /* exp.AuthErrorCode.INVALID_API_KEY */, {
      appName: app.name
    });
    this._delegate = new exp.RecaptchaVerifier(container,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parameters,
    // TODO: remove ts-ignore when moving types from auth-types to auth-compat
    // @ts-ignore
    app.auth());
    this.type = this._delegate.type;
  }
  _createClass(RecaptchaVerifier, [{
    key: "clear",
    value: function clear() {
      this._delegate.clear();
    }
  }, {
    key: "render",
    value: function render() {
      return this._delegate.render();
    }
  }, {
    key: "verify",
    value: function verify() {
      return this._delegate.verify();
    }
  }]);
  return RecaptchaVerifier;
}();
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var AUTH_TYPE = 'auth-compat';
// Create auth components to register with firebase.
// Provides Auth public APIs.
function registerAuthCompat(instance) {
  instance.INTERNAL.registerComponent(new Component(AUTH_TYPE, function (container) {
    // getImmediate for FirebaseApp will always succeed
    var app = container.getProvider('app-compat').getImmediate();
    var authProvider = container.getProvider('auth');
    return new Auth(app, authProvider);
  }, "PUBLIC" /* ComponentType.PUBLIC */).setServiceProps({
    ActionCodeInfo: {
      Operation: {
        EMAIL_SIGNIN: exp.ActionCodeOperation.EMAIL_SIGNIN,
        PASSWORD_RESET: exp.ActionCodeOperation.PASSWORD_RESET,
        RECOVER_EMAIL: exp.ActionCodeOperation.RECOVER_EMAIL,
        REVERT_SECOND_FACTOR_ADDITION: exp.ActionCodeOperation.REVERT_SECOND_FACTOR_ADDITION,
        VERIFY_AND_CHANGE_EMAIL: exp.ActionCodeOperation.VERIFY_AND_CHANGE_EMAIL,
        VERIFY_EMAIL: exp.ActionCodeOperation.VERIFY_EMAIL
      }
    },
    EmailAuthProvider: exp.EmailAuthProvider,
    FacebookAuthProvider: exp.FacebookAuthProvider,
    GithubAuthProvider: exp.GithubAuthProvider,
    GoogleAuthProvider: exp.GoogleAuthProvider,
    OAuthProvider: exp.OAuthProvider,
    SAMLAuthProvider: exp.SAMLAuthProvider,
    PhoneAuthProvider: PhoneAuthProvider,
    PhoneMultiFactorGenerator: exp.PhoneMultiFactorGenerator,
    RecaptchaVerifier: RecaptchaVerifier,
    TwitterAuthProvider: exp.TwitterAuthProvider,
    Auth: Auth,
    AuthCredential: exp.AuthCredential,
    Error: FirebaseError
  }).setInstantiationMode("LAZY" /* InstantiationMode.LAZY */).setMultipleInstances(false));
  instance.registerVersion(name, version);
}
registerAuthCompat(firebase);